import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import Drawer from '@material-ui/core/Drawer';
import styled from 'styled-components'
import { withStyles } from '@material-ui/core/styles'
import { sideMenuWidth, headerMenuHeight, breakPoint } from '../MuiTheme'
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import FolderIcon from '@material-ui/icons/Folder'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import { bgDark, bgLight, blue } from '../Colors'
import ArrowRight from '@material-ui/icons/KeyboardArrowRight'
import ArrowDown from '@material-ui/icons/KeyboardArrowDown'
import {Link} from 'react-router-dom'
import {LocaleSwitcher} from '../LocaleSwitcher/LocaleSwitcher'
import { FormattedMessage, injectIntl } from 'react-intl'
import {t} from '../Intl'

const Contents = styled.div.attrs({
  className: 'tr-sidemenu-contents'
})`
  margin-top: calc(${headerMenuHeight}px + .8rem);
  width: ${sideMenuWidth}px;

  /* 9007199254740991 is Number.MAX_SAFE_INTEGER value (IE doesn't support Number.MAX_SAFE_INTEGER) */
  @media(max-width: ${ p => p.hidePermanent ? 9007199254740991 : breakPoint}px){
    margin-top: .8rem;
  }
`

const Title = styled.div.attrs({
  className: 'tr-sidemenu-title'
})`
  text-transform: uppercase;
  width: 100%;
  padding-bottom: .6rem;
  font-weight: 500;
  padding-left: 1rem;
  border-bottom: 1px solid ${bgDark};
`

const Foot = styled.div.attrs({
  className: 'tr-sidemenu-footer'
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  padding: ${({showLocaleSwitcher}) => showLocaleSwitcher ? '0.1rem 0.5rem' : '0.5rem'} ;
  bottom: 0;
  font-size: 0.8rem;
  background: ${bgLight};
  width: 100%;
  border-top: 1px solid ${bgDark};
  a {
    text-decoration: none;
    color: ${blue};
  }
`

const styles = theme => ({
  temporary: {
    [theme.breakpoints.up(999)]: {
      display: 'none'
    }
  },
  permanent: {
    [theme.breakpoints.down(999)]: {
      display: 'none'
    }
  },
  menuitem: {
    borderBottom: `1px solid ${bgDark}`,
    padding: '0.5rem',
    paddingLeft: '0.8rem'
  },
  list: {
    padding: 0
  },
})


class Menu extends Component
{
  static propTypes = {
    menu: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.object)
    ]).isRequired,
    activeMenuItem: PropTypes.any,
    title: PropTypes.oneOfType([
      PropTypes.shape({
        messageId: PropTypes.string.isRequired,
        defaultMessage: PropTypes.string.isRequired
      }),
      PropTypes.node
    ]).isRequired,
    open: PropTypes.bool.isRequired,
    hidePermanent: PropTypes.bool,
    locale: PropTypes.shape({
      id: PropTypes.string,
      locale: PropTypes.string
    }).isRequired,
    locales: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      locale: PropTypes.string
    })).isRequired,
    onLocaleChange: PropTypes.func,
    showLocaleSwitcher: PropTypes.bool,
    loadingLocale: PropTypes.bool
  }

  static defaultProps = {
    hidePermanent: false,
    showLocaleSwitcher: true,
    loadingLocale: false
  }

  activeItemIsDescendant = children => {
    if(!children || !children.length) return false;
    const { activeMenuItem } = this.props
    for( let i = 0; i < children.length; i++)
    {
      const child = children[i]
      if(child === activeMenuItem) return true
      if(child.submenus && this.activeItemIsDescendant(child.submenus)) return true
    }
    return false;
  }

  getInitalState = items => {
    if(React.isValidElement(items)) return {}
    const { activeMenuItem } = this.props

    let idx = 0
    const doIt = (myItems, acc = {} ) => {
      myItems.forEach( item => {
        if(item.submenus)
        {
          acc[idx] = activeMenuItem === item || this.activeItemIsDescendant(item.submenus)
          idx++
          doIt(item.submenus, acc)
        }
        else idx++
      })
      return acc
    }
    return doIt(items)
  }


  state = {
    nodes: this.getInitalState(this.props.menu)
  }

  onMenuClick = e => {
    const idx = parseInt(e.currentTarget.getAttribute('data-idx'))
    const item = this.items[idx]

    if( item.submenus)
    {
      this.setState( state => ({
        nodes: {
          ...state.nodes,
          [idx]: !state.nodes[idx]
        }
      }))
    }

    if(item.onClick) {
      item.onClick(item)
    }
    if(!item.submenus)
    {
      this.props.close()
    }
  }

  items = []

  isActive = item => {
    const { activeMenuItem } = this.props
    const { nodes } = this.state
    const idx = this.items.length - 1

    const active = activeMenuItem === item
    const activeChild = this.activeItemIsDescendant(item.submenus)

    if(!item.submenus ) return active
    if( active || activeChild ) return nodes[idx] !== false
    return !!nodes[idx]
  }

  getIcon = (item, active) => {
    if(active)
    {
      return item.activeIcon || (item.submenus ? <ArrowDown/> : <FolderOpenIcon/>)
    }
    return item.icon || (item.submenus ? <ArrowRight/> : <FolderIcon/>)
  }

  renderMenuItemsWithBuilder = (menuItems, depth=0) => {
    if(depth === 0) this.items = []
    const { classes, activeMenuItem, intl } = this.props
      return menuItems.map( item => {
        this.items.push(item)

        const isActive = this.isActive(item)

        const style = {}
        if(depth > 0 ) style.paddingLeft = `${depth * 30 }px`
        if( activeMenuItem === item && !item.disableHighlight && !item.submenus) style.backgroundColor = '#eaf2f5'

        const itemProps = {
          'data-idx': this.items.length - 1,
          className: classes.menuitem,
          'data-test': `sidemenu-item-${item.messageId}`,
          'onClick' : this.onMenuClick,
          style
        }

        if( item.link )
        {
          itemProps.component = Link
          itemProps.to = item.link
        }
        if(item.externalLink)
        {
          itemProps.component = 'a'
          itemProps.href = item.externalLink.link
          if( item.externalLink.newWindow )
          {
            itemProps.target = '_blank'
          }
        }

        return (
          <Fragment key={this.items.length - 1}>
            <MenuItem {...itemProps}>
              <ListItemIcon>
                { this.getIcon(item, isActive) }
              </ListItemIcon>
              <ListItemText primary={intl.formatMessage(t(item.messageId, item.defaultMessage))}/>
            </MenuItem>
            { item.submenus &&  isActive && this.renderMenuItemsWithBuilder(item.submenus, depth+1)}
          </Fragment>
        )
      })
  }

  contents = () => {
    const { title, menu, hidePermanent, intl } = this.props
    return (
      <Contents hidePermanent={hidePermanent}>
        {
          React.isValidElement(title) ?
            title :
            <Title>
              { intl.formatMessage(t(title.messageId, title.defaultMessage)) }
            </Title>
        }
        {
          React.isValidElement(menu) ?
           menu :
           this.renderMenuItemsWithBuilder(menu)
        }
      </Contents>
    )
  }

  footer = () =>
  {
    const { showLocaleSwitcher, locale, onLocaleChange, loadingLocale, locales } = this.props
    return (
      <Foot showLocaleSwitcher={showLocaleSwitcher}>
        { showLocaleSwitcher ?
          (<LocaleSwitcher value={locale}  locales={locales} onLocaleChange={onLocaleChange} loading={loadingLocale}/>) :
          (<div>© {new Date().getFullYear()} talentReef Inc.</div>)
        }
        <a
          href="https://www.talentreef.com/talentreef-privacy-policy"
          rel="noopener"
          target="_blank"
          title="view privacy policy"
        >
          <FormattedMessage id="tr.layout.privacy" defaultMessage="Privacy"/>
        </a>
    </Foot>
    )
  }


  render()
  {
    const { classes, open, hidePermanent } = this.props
    return (
      <Fragment>
          {!hidePermanent && (
            <Drawer variant="permanent" className={classes.permanent} data-test='sidemenu-perm'>
              {this.contents()}
              {this.footer()}
            </Drawer>
          )}
          <Drawer open={open} className={hidePermanent ? undefined : classes.temporary} onClose={this.props.close} data-test='sidemenu'>
            { open && (
              <Fragment>
                {this.contents()}
                {this.footer()}
              </Fragment>
            )}
          </Drawer>
      </Fragment>
    )
  }
}

export const SideMenu = injectIntl(withStyles(styles)(Menu))


// WEBPACK FOOTER //
// ../../src/Layout/SideMenu.js